/**
 * Shared constants across all packages.
 * Single source of truth for magic strings and config values.
 */
// ---------------------------------------------------------------------------
// Firestore Collection Paths
// ---------------------------------------------------------------------------
// W14 domain 5 census: v1-era keys with no v2 reader/writer removed
// (HALL_OF_FAME — killed feature; SCENARIOS/LOCATIONS/SOLO_OPERATORS/
// COMMUNITY_REQUESTS/ZIP_DEPARTMENT_CACHE — v1 collections v2 never used;
// zip cache belonged to the cut map discovery surface).
export const COLLECTIONS = {
    OFFICERS: 'officers',
    DEPARTMENTS: 'departments',
    PUBLIC_SESSIONS: 'publicSessions',
    SHARED_REPORTS: 'sharedReports',
    INVITATIONS: 'invitations',
    PUBLIC_COMMUNITY_CONNECT: 'public_community_connect',
    PUBLIC_CIVILIAN_CHALLENGES: 'public_civilian_challenges',
    USERS_LOOKUP: 'users_lookup',
    BREAKDOWNS: 'breakdowns',
    SCENARIO_ARCHETYPES: 'scenario_archetypes',
    MATCHES: 'matches',
    WEEKLY_DRILLS: 'weekly_drills',
    MODE_EVIDENCE: 'mode_evidence',
    SESSION_FEEDBACK: 'session_feedback',
    APP_FEEDBACK: 'app_feedback',
    USAGE_EVENTS: 'usage_events',
    USAGE_DAILY: 'usage_daily',
    AI_USAGE: 'ai_usage',
    AI_USAGE_DAILY: 'ai_usage_daily',
    SPECIAL_EVENTS: 'special_events',
    PLATFORM_ADMINS: 'platform_admins',
    AUDIT_EVENTS: 'audit_events',
    STRIPE_EVENTS: 'stripe_events',
    CONTACT_REQUESTS: 'contact_requests',
    CONTACT_RATE: 'contact_rate',
    EMAIL_LEDGER: 'email_ledger',
    // CRM (W22) — founder outreach instrument on /ops
    CRM_AGENCIES: 'crm_agencies',
    CRM_INDEX: 'crm_index',
    CRM_OUTBOX: 'crm_outbox',
    CRM_SUPPRESSION: 'crm_suppression',
    CRM_SETTINGS: 'crm_settings',
};
export const SUBCOLLECTIONS = {
    SESSIONS: 'sessions',
    DIALOGUE: 'dialogue',
    POST_SIMULATION_ANALYSIS: 'postSimulationAnalysis',
    DOJO_SCENARIOS: 'dojo_scenarios',
    DOJO_ASSIGNMENTS: 'dojo_assignments',
    DOJO_SUPPLEMENTS: 'dojo_supplements',
    INSIGHTS_DEEP: 'insights_deep',
    INSIGHTS_SESSION: 'insights_session',
    INSIGHTS_AGGREGATED: 'insights_aggregated',
    MESSAGES: 'messages',
    THREADS: 'threads',
    AUDIT_LOG: 'audit_log',
    TRAINING_SESSIONS: 'training_sessions',
    COMPLETIONS: 'completions',
    INVITE_LINKS: 'invite_links',
    SENT_INVITES: 'sent_invites',
    SESSION_REPORTS: 'session_reports',
    BASED_TAKES: 'based_takes',
    SESSION_PARTICIPANTS: 'session_participants',
    /** crm_agencies/{id}/log — append-only commit log (W22). */
    CRM_LOG: 'log',
};
// ---------------------------------------------------------------------------
// Simulation Constants
// ---------------------------------------------------------------------------
export const SIMULATION = {
    /** Max turns before forced session end */
    MAX_TURNS: 100,
    /** Token budget for context window */
    MAX_CONTEXT_TOKENS: 200_000,
    /** Revelation budget per turn by category */
    REVELATION_BUDGET: {
        MICRO: 1,
        MINOR: 2,
        MAJOR: 4,
    },
};
// ---------------------------------------------------------------------------
// AI (xAI / Grok) — July 2026 API surface
// ---------------------------------------------------------------------------
export const AI = {
    BASE_URL: 'https://api.x.ai/v1',
    /**
     * Single text model; per-call reasoning effort replaces the old
     * fast-reasoning / fast-non-reasoning model split.
     */
    MODEL: 'grok-4.3',
    IMAGE_MODEL: 'grok-imagine-image',
};
// ---------------------------------------------------------------------------
// Certification Constants
// ---------------------------------------------------------------------------
export const CERTIFICATION = {
    /** Days from 40 → 0 freshness */
    FRESHNESS_DECAY_DAYS: 40,
    /** Sessions in rolling average per tier */
    ROLLING_WINDOW: {
        BRONZE: 5,
        SILVER: 10,
        GOLD: 20,
        PLATINUM: 35,
        DIAMOND: 50,
    },
    /**
     * Score thresholds for tier advancement, on the /10 report scale — the
     * ONE scale officers ever see ("Final Rating: X/10", drill pass targets,
     * insight pattern scores). Historical note: these briefly lived as
     * percent values (60-90) while every producer scored /10, which pinned
     * every officer at Bronze; certification.ts is the tier authority now.
     */
    TIER_THRESHOLDS: {
        BRONZE: 0,
        SILVER: 6,
        GOLD: 7,
        PLATINUM: 8,
        DIAMOND: 9,
    },
};
// ---------------------------------------------------------------------------
// Insight Dimensions
// ---------------------------------------------------------------------------
export const INSIGHT_DIMENSIONS = [
    'empathy',
    'awareness',
    'patience',
    'problemSolving',
    'processOutcomeBalance',
    'communityFocus',
    'proceduralAdherence',
    'stressManagement',
    'deescalationPriority',
    'ethics',
];
// ---------------------------------------------------------------------------
// Civilian Challenge Constants
// ---------------------------------------------------------------------------
export const CIVILIAN_CHALLENGE = {
    /** Days before access token expires */
    TOKEN_EXPIRY_DAYS: 7,
    /** Max turns for civilian attempt */
    MAX_TURNS: 30,
    /** Attempts allowed per anonymous user per challenge */
    MAX_ATTEMPTS_PER_USER: 2,
    /** Max characters per civilian turn input */
    INPUT_MAX_CHARS: 1200,
    /** Entries kept on the denormalized public leaderboard */
    LEADERBOARD_SIZE: 25,
};
//# sourceMappingURL=index.js.map